import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Link2,
  Plus,
  Copy,
  MoreHorizontal,
  Trash2,
  Calendar,
  Users,
  Check,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react'
import type { TemplateShareLink } from '@/lib/api/types'
import { getPermissionLevelConfig } from '@/lib/api/types'
import { templateShareLinkService } from '@/lib/api/services/templateShareLinks'
import { useToast } from '@/components/ui/use-toast'
import { CreateShareLinkDialog } from './CreateShareLinkDialog'

interface ShareLinksListProps {
  templateId: string
  templateName: string
  shareLinks: TemplateShareLink[]
  onShareLinksChange: () => void
}

export function ShareLinksList({
  templateId,
  templateName,
  shareLinks,
  onShareLinksChange,
}: ShareLinksListProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [linkToRevoke, setLinkToRevoke] = useState<TemplateShareLink | null>(
    null
  )
  const [revoking, setRevoking] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const { toast } = useToast()

  const handleRevoke = async () => {
    if (!linkToRevoke) return

    setRevoking(true)
    try {
      await templateShareLinkService.revokeShareLink(templateId, linkToRevoke.id)
      toast({
        title: 'Link revoked',
        description: 'The share link has been revoked and can no longer be used',
      })
      onShareLinksChange()
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to revoke share link',
        variant: 'destructive',
      })
    } finally {
      setRevoking(false)
      setLinkToRevoke(null)
    }
  }

  const handleCopyLink = async (shareLink: TemplateShareLink) => {
    if (!shareLink.shareUrl) return

    try {
      await navigator.clipboard.writeText(shareLink.shareUrl)
      setCopiedId(shareLink.id)
      toast({
        title: 'Link copied',
        description: 'Share link has been copied to your clipboard',
      })
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy link to clipboard',
        variant: 'destructive',
      })
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const activeLinks = shareLinks.filter((l) => l.isActive)

  return (
    <div className="space-y-4">
      {/* Header with Create button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {activeLinks.length} active share link
          {activeLinks.length !== 1 && 's'}
        </p>
        <Button
          size="sm"
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Link
        </Button>
      </div>

      {/* Share Links list */}
      <div className="space-y-3">
        {activeLinks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Link2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No share links created yet</p>
            <Button
              variant="link"
              size="sm"
              onClick={() => setShowCreateDialog(true)}
              className="mt-2"
            >
              Create your first share link
            </Button>
          </div>
        ) : (
          activeLinks.map((shareLink) => {
            const levelConfig = getPermissionLevelConfig(
              shareLink.permissionLevel
            )
            const isExpiringSoon =
              shareLink.expiresAt &&
              new Date(shareLink.expiresAt).getTime() - Date.now() <
                7 * 24 * 60 * 60 * 1000

            return (
              <div
                key={shareLink.id}
                className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="p-2 rounded-full bg-muted flex-shrink-0">
                      <Link2 className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">
                          {shareLink.name || 'Unnamed Link'}
                        </span>
                        <Badge
                          className={`${levelConfig.bgColor} ${levelConfig.textColor} ${levelConfig.darkBgColor} ${levelConfig.darkTextColor} flex-shrink-0`}
                        >
                          {levelConfig.label}
                        </Badge>
                        {shareLink.requiresAuthentication && (
                          <Badge variant="outline" className="flex-shrink-0">
                            Requires Login
                          </Badge>
                        )}
                      </div>

                      {/* Link details */}
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                        {shareLink.expiresAt && (
                          <span
                            className={`flex items-center gap-1 ${isExpiringSoon ? 'text-amber-600 dark:text-amber-400' : ''}`}
                          >
                            {isExpiringSoon && (
                              <AlertTriangle className="h-3 w-3" />
                            )}
                            <Calendar className="h-3 w-3" />
                            Expires {formatDate(shareLink.expiresAt)}
                          </span>
                        )}
                        {shareLink.maxUses && (
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {shareLink.usedCount}/{shareLink.maxUses} uses
                          </span>
                        )}
                        {shareLink.allowedDomains && (
                          <span className="truncate max-w-[150px]">
                            Domains: {shareLink.allowedDomains}
                          </span>
                        )}
                      </div>

                      {/* Share URL preview */}
                      {shareLink.shareUrl && (
                        <div className="mt-2 flex items-center gap-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-[250px]">
                            {shareLink.shareUrl}
                          </code>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyLink(shareLink)}
                      className="h-8"
                    >
                      {copiedId === shareLink.id ? (
                        <>
                          <Check className="h-4 w-4 mr-1 text-green-600" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {shareLink.shareUrl && (
                          <DropdownMenuItem
                            onClick={() =>
                              window.open(shareLink.shareUrl, '_blank')
                            }
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open Link
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => setLinkToRevoke(shareLink)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Revoke Link
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Create Share Link Dialog */}
      <CreateShareLinkDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        templateId={templateId}
        templateName={templateName}
        onSuccess={onShareLinksChange}
      />

      {/* Revoke Confirmation Dialog */}
      <AlertDialog
        open={!!linkToRevoke}
        onOpenChange={(open) => !open && setLinkToRevoke(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Share Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this share link? Anyone with this
              link will no longer be able to access the template.
              {linkToRevoke?.usedCount ? (
                <span className="block mt-2 text-amber-600 dark:text-amber-400">
                  This link has been used {linkToRevoke.usedCount} time
                  {linkToRevoke.usedCount !== 1 && 's'}.
                </span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              disabled={revoking}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revoking ? 'Revoking...' : 'Revoke Link'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
